import React from 'react';
import { VideoSettings, Provider } from '../types/video';
import { useIsMobile } from '../hooks/useMediaQuery';

interface VideoSettingsPanelProps {
  settings: VideoSettings;
  setSettings: React.Dispatch<React.SetStateAction<VideoSettings>>;
  quality: 'fast' | 'quality';
  setQuality: React.Dispatch<React.SetStateAction<'fast' | 'quality'>>;
  selectedProvider?: Provider;
  onProviderChange: (provider?: Provider) => void;
  scenarioDescription: string;
  isGenerating?: boolean;
  disabled?: boolean;
}

const PROVIDERS: { value: Provider | 'auto'; label: string; description: string }[] = [
  { value: 'auto', label: 'Automatisch', description: 'Laat VidGenTF automatisch kiezen' },
  { value: 'sora', label: 'Sora 2', description: 'OpenAI’s geavanceerde video generator' },
  { value: 'runway', label: 'RunwayML', description: 'Professionele video creatie tools' },
  { value: 'veo', label: 'Veo 3', description: 'Google’s high-quality video generator' },
  { value: 'mock', label: 'Test', description: 'Gratis test mode voor demo doeleinden' },
];

const ASPECT_RATIOS = [
  { value: '9:16', label: '9:16', description: 'Portret (Instagram, TikTok, Shorts)' },
  { value: '16:9', label: '16:9', description: 'Landschap (YouTube, TV)' },
];

const DURATIONS = [5, 8, 10, 12, 15, 20, 25, 30];

const WEATHER_OPTIONS = [
  { value: 'zonnig', label: 'Zonnig' },
  { value: 'bewolkt', label: 'Bewolkt' },
  { value: 'regen', label: 'Regen' },
  { value: 'mist', label: 'Mist' },
  { value: 'nacht', label: 'Nacht' },
];

const TIME_OPTIONS = [
  { value: 'overdag', label: 'Overdag' },
  { value: "'s avonds", label: "'s Avonds" },
  { value: "'s nachts", label: "'s Nachts" },
];

const STYLE_OPTIONS = [
  { value: 'realistisch', label: 'Realistisch' },
  { value: 'instructief', label: 'Instructief' },
  { value: 'cartoon', label: 'Cartoon' },
];

const QUALITY_OPTIONS = [
  { value: 'fast', label: 'Snel', description: '2-3 minuten, lagere kosten' },
  { value: 'quality', label: 'Hoge Kwaliteit', description: '5-7 minuten, beste kwaliteit' },
];

interface SelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; description?: string }>;
  disabled?: boolean;
}

const Select: React.FC<SelectProps> = ({ label, value, onChange, options, disabled }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-1 min-w-0" ref={dropdownRef}>
      <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`dropdown w-full text-left text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span className="truncate">{selectedOption.label}</span>
        <svg
          className="w-4 h-4 transition-transform flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[var(--color-muted)] transition-colors ${
                  option.value === value ? 'bg-[var(--color-muted)] text-[var(--color-primary)]' : 'text-[var(--color-text-primary)]'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate">{option.label}</div>
                  {option.description && (
                    <div className="text-xs text-[var(--color-text-secondary)] truncate">{option.description}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const VideoSettingsPanel: React.FC<VideoSettingsPanelProps> = ({
  settings,
  setSettings,
  quality,
  setQuality,
  selectedProvider,
  onProviderChange,
  scenarioDescription,
  isGenerating = false,
  disabled = false,
}) => {
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = React.useState(isMobile);

  React.useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);

  const updateSetting = (key: keyof VideoSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const controlsDisabled = disabled || isGenerating;

  return (
    <div className="card p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Video Instellingen
            </h2>
            <p className="text-xs text-[var(--color-text-secondary)]">
              Stel camerawerk, stijl en provider af voor je scenario
            </p>
          </div>
        </div>

        {isMobile && (
          <button
            type="button"
            className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            onClick={() => setIsCollapsed(prev => !prev)}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? 'Openen' : 'Inklappen'}
          </button>
        )}
      </div>

      {(!isMobile || !isCollapsed) && (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Provider"
              value={selectedProvider || 'auto'}
              onChange={(value) => onProviderChange(value === 'auto' ? undefined : (value as Provider))}
              options={PROVIDERS}
              disabled={controlsDisabled}
            />
            <Select
              label="Formaat"
              value={settings.aspectRatio || '9:16'}
              onChange={(value) => updateSetting('aspectRatio', value)}
              options={ASPECT_RATIOS}
              disabled={controlsDisabled}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Kwaliteit"
              value={quality}
              onChange={(value) => setQuality(value as 'fast' | 'quality')}
              options={QUALITY_OPTIONS}
              disabled={controlsDisabled}
            />
            <Select
              label="Duur"
              value={settings.durationSeconds?.toString() || '15'}
              onChange={(value) => updateSetting('durationSeconds', parseInt(value, 10))}
              options={DURATIONS.map(duration => ({
                value: duration.toString(),
                label: `${duration}s`,
              }))}
              disabled={controlsDisabled}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Select
              label="Weer"
              value={settings.weather || 'zonnig'}
              onChange={(value) => updateSetting('weather', value)}
              options={WEATHER_OPTIONS}
              disabled={controlsDisabled}
            />
            <Select
              label="Tijdstip"
              value={settings.timeOfDay || 'overdag'}
              onChange={(value) => updateSetting('timeOfDay', value)}
              options={TIME_OPTIONS}
              disabled={controlsDisabled}
            />
            <Select
              label="Stijl"
              value={settings.style || 'realistisch'}
              onChange={(value) => updateSetting('style', value)}
              options={STYLE_OPTIONS}
              disabled={controlsDisabled}
            />
          </div>

          <Select
            label="Camerahoek"
            value={settings.cameraAngle || 'dashboard'}
            onChange={(value) => updateSetting('cameraAngle', value)}
            options={[
              { value: 'dashboard', label: 'Dashboard', description: 'Bestuurder perspectief' },
              { value: 'drone', label: 'Drone', description: 'Luchtbeeld' },
              { value: 'voetganger', label: 'Voetganger', description: 'Straat niveau' },
              { value: 'passant', label: 'Passant', description: 'Zijstraat perspectief' },
            ]}
            disabled={controlsDisabled}
          />

          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-alt)] p-4 text-xs text-[var(--color-text-secondary)] leading-relaxed">
            <p className="font-medium text-[var(--color-text-primary)] mb-1">Scenario klaar?</p>
            <p>
              Controleer je instellingen en genereer de video zodra je tevreden bent.
              Je gekozen scenario bevat {scenarioDescription.trim().length} karakters.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoSettingsPanel;
